import './Recruit.scss'
import './RecruitList.scss'
import { roleMarkerClass } from '../../../utils/roleMarker'
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { MdArrowForwardIos, MdArrowBack } from 'react-icons/md'
import MainURL from '../../../MainURL'
import axios from 'axios'
import { useAtomValue } from 'jotai'
import { loginStateAtom, userDataAtom } from '../../../AtomStore'
import type { RecruitBoardConfig } from './RecruitTypes'
import type { RecruitDetailLocationState } from './recruitNavigation'
import { safeJsonParse, sortLabelPrefix } from './recruitUtils'
import RecruitApplyModal from './RecruitApplyModal'
import RecruitApplyPanel from './RecruitApplyPanel'
import { applyhowWithSite, withSiteApplyFact } from './applyMethods'
import RecruitShareKit from './RecruitShareKit'
import type { ShareKitData } from './RecruitShareKit'
import { useApplyChannel } from './useApplyChannel'
import { useAggregatedInfo } from './useAggregatedInfo'
import AggregatedLockBanner, { AggregatedSourceNotice } from './AggregatedLockBanner'
import OverseasGoogleMap from '../recruit_overseas/OverseasGoogleMap'

const DEFAULT_SORT_ITEM = [{ sort: '전임', content: '' }]
const DEFAULT_WORK_TIME_SUN = [{ sort: '전임', startHour: '09', startMinute: '00', endHour: '16', endMinute: '00' }]
const DEFAULT_WORK_TIME_WEEK = [{ sort: '전임', startHour: '09', startMinute: '00', endHour: '17', endMinute: '00', day: '평일' }]
const DEFAULT_PAY = [{ sort: '전임', paySort: '', selectCost: '', inputCost: '' }]
// 작성 폼 드롭다운의 미선택 값 'select' 는 화면에 보이지 않게 비운다 (서버 recruitPay.js 와 같은 기준)
const cleanPaySort = (list: any) =>
  Array.isArray(list) ? list.map((p: any) => (p && p.paySort === 'select' ? { ...p, paySort: '' } : p)) : list
const DEFAULT_APPLYTIME = { startDay: '', endDay: '', daySort: '' }
const DEFAULT_INQUIRY = { inquiryName: '', email: '', phone: '' }

export default function RecruitDetail({ config }: { config: RecruitBoardConfig }) {
  const params = useParams()
  const ID = params.id ?? new URL(window.location.href).searchParams.get('id')
  const navigate = useNavigate()
  const locationHook = useLocation()
  const recruitState = (locationHook.state as RecruitDetailLocationState | null)?.recruitState

  const [part, setPart] = useState<any>(DEFAULT_SORT_ITEM)
  const [partDetail, setPartDetail] = useState<any>(DEFAULT_SORT_ITEM)
  const [school, setSchool] = useState<any>(DEFAULT_SORT_ITEM)
  const [career, setCareer] = useState<any>(DEFAULT_SORT_ITEM)
  const [workday, setWorkday] = useState<any>(DEFAULT_SORT_ITEM)
  const [workTimeSunDay, setWorkTimeSunDay] = useState<any>(DEFAULT_WORK_TIME_SUN)
  const [workTimeWeek, setWorkTimeWeek] = useState<any>(DEFAULT_WORK_TIME_WEEK)
  const [dawnPray, setDawnPray] = useState<any>(DEFAULT_SORT_ITEM)
  const [pay, setPay] = useState<any>(DEFAULT_PAY)
  const [insurance, setInsurance] = useState<any>(DEFAULT_SORT_ITEM)
  const [severance, setSeverance] = useState<any>(DEFAULT_SORT_ITEM)
  const [welfare, setWelfare] = useState<any>(DEFAULT_SORT_ITEM)
  const [applytime, setApplytime] = useState<any>(DEFAULT_APPLYTIME)
  const [applydoc, setApplydoc] = useState<any>(DEFAULT_SORT_ITEM)
  const [inquiry, setInquiry] = useState<any>(DEFAULT_INQUIRY)
  const [customInput, setCustomInput] = useState('')

  const [title, setTitle] = useState('')
  const [link, setLink] = useState('')
  const [source, setSource] = useState('')
  const [autoPublished, setAutoPublished] = useState(false)
  const [sort, setSort] = useState('')
  const [date, setDate] = useState('')
  const [location, setLocation] = useState('')
  const [locationDetail, setLocationDetail] = useState('')
  const [applyhow, setApplyhow] = useState('')
  const [etcNotice, setEtcNotice] = useState('')
  const [church, setChurch] = useState('')
  const [churchLogo, setChurchLogo] = useState('')
  const [churchProfileId, setChurchProfileId] = useState<number | null>(null)
  const [religiousbody, setReligiousbody] = useState('')
  const [deadlineAt, setDeadlineAt] = useState('')
  const [address, setAddress] = useState('')
  const [mainPastor, setMainPastor] = useState('')
  const [homepage, setHomepage] = useState('')

  const [mapLoaded, setMapLoaded] = useState(true)
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false)
  const [isShareKitOpen, setIsShareKitOpen] = useState(false)
  const [viewState, setViewState] = useState<'loading' | 'ok' | 'notFound' | 'deleted'>('loading')
  const [recruitStatus, setRecruitStatus] = useState<'OPEN' | 'CLOSED'>('OPEN')

  const mapElement = useRef<HTMLDivElement | null>(null)
  const { naver } = window

  const isLogin = useAtomValue(loginStateAtom)
  const userData = useAtomValue(userDataAtom)
  const applyType = config.tableType === 'overseas' ? 'overseas' : 'minister'
  // 지원 경로 — 교회 이메일이 있으면 로그인 없이 파일로 지원 (in-system-apply-strategy.md)
  const channel = useApplyChannel(config.canApply && viewState === 'ok' ? applyType : null, ID)
  const canApply = Boolean(config.canApply) && recruitStatus !== 'CLOSED'
    && Boolean(channel && channel.state === 'open' && (channel.fileApply || channel.resumeApply))
  // 가져온 공고의 잠금 정보 + 조회수 (aggregated-posting-lock-design.md) — 원문 로고·사진·개인 휴대폰은 표시하지 않는다 (계층 C)
  const aggKind = config.tableType || config.simplePostType || null
  const aggInfo = useAggregatedInfo(viewState === 'ok' ? aggKind : null, ID)
  const aggManaged = Boolean(aggInfo?.managed)
  const aggLocked = Boolean(aggInfo?.locked)
  const shownLogo = aggManaged ? '' : churchLogo
  // 가져온 공고 재작성 본문의 '지원 방법'은 사이트 지원 안내로 (application-structure-final.md 7.1 1번)
  const shownCustomInput = (() => {
    const html = aggManaged ? customInput.replace(/<img\b[^>]*>/gi, '') : customInput
    return autoPublished && canApply ? withSiteApplyFact(html) : html
  })()
  const shownPhone = aggLocked && /^01\d/.test(String(inquiry.phone || '').replace(/\D/g, '')) ? '' : inquiry.phone
  const useGoogleMap = config.mapProvider === 'google'

  const handleApplyClick = () => {
    setIsApplyModalOpen(true)
  }

  // 검색·공유 링크의 ?apply=1 → 지원 창을 바로 연다
  const autoApplyDone = useRef(false)
  useEffect(() => {
    if (autoApplyDone.current || !canApply) return
    if (new URLSearchParams(locationHook.search).get('apply') === '1') {
      autoApplyDone.current = true
      setIsApplyModalOpen(true)
    }
  }, [canApply, locationHook.search])

  // 공고 고유 URL — 서버가 카카오톡 등 링크 미리보기(OG)와 검색용 메타를 붙여 응답한다
  const shareUrl = `https://www.ministermore.co.kr${config.detailPath}/${ID}`
  const [copied, setCopied] = useState(false)
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
    } catch {
      // clipboard API 미지원(구형 브라우저/웹뷰) 폴백
      const textarea = document.createElement('textarea')
      textarea.value = shareUrl
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const nativeShare = async () => {
    try {
      await navigator.share({ title, text: title, url: shareUrl })
    } catch (error) {
      // 사용자가 공유창을 닫은 경우(AbortError)는 무시
      if ((error as Error)?.name !== 'AbortError') copyShareUrl()
    }
  }

  const payText = (() => {
    const first = pay[0] || {}
    return (first.inputCost || [first.paySort, first.selectCost].filter(Boolean).join(' ') || '').trim()
  })()
  const shareKitData: ShareKitData = {
    code: config.tableType === 'overseas' ? 'o' : config.tableType === 'minister' ? 'm' : config.simplePostType === 'church' ? 'c' : 'i',
    id: ID || '',
    title,
    church,
    religiousbody,
    region: [location, locationDetail].filter((v, i, arr) => v && arr.indexOf(v) === i && !(i > 0 && arr[0].includes(v))).join(' '),
    role: [sort, part[0]?.content, partDetail[0]?.content].filter(Boolean).join(' · '),
    pay: config.showMinistryDetail ? payText : '',
    deadline: deadlineAt || (applytime.endDay ? applytime.endDay : applytime.daySort || ''),
    applydoc: (applydoc || []).map((d: any) => d?.content).filter(Boolean).join(', '),
    siteApply: canApply,
  }

  const goToList = () => {
    if (recruitState) {
      navigate(config.listPath, { state: { recruitState } })
      return
    }

    navigate(config.listPath)
  }

  const addressAPI = async (addressQuery: string) => {
    try {
      const response = await axios.post(`${MainURL}/${config.apiBase}/geocode`, {
        address: addressQuery,
      })

      if (response.data.success && response.data.coordinates) {
        const { latitude, longitude } = response.data.coordinates

        if (!mapElement.current || !naver) {
          setMapLoaded(false)
          return
        }

        const mapLocation = new naver.maps.LatLng(latitude, longitude)
        const mapOptions = {
          center: mapLocation,
          zoom: 12,
          zoomControl: true,
        }
        const map = new naver.maps.Map(mapElement.current, mapOptions)
        new naver.maps.Marker({
          position: mapLocation,
          map,
        })
        setMapLoaded(true)
      } else {
        setMapLoaded(false)
      }
    } catch (error) {
      console.error('지오코딩 API 오류:', error)
      setMapLoaded(false)
    }
  }

  const fetchPosts = async () => {
    const res = await axios.post(`${MainURL}/${config.apiBase}/getrecruitdatapart`, {
      id: ID,
    })
    if (!res.data) {
      setViewState('notFound')
      return
    }

    const copy = { ...res.data[0] }

    // 삭제 요청으로 내려간 가져온 공고도 삭제 안내로 (aggregatedLock.js — unpublishReason REJECTED)
    if (copy.deletedAt || (copy.unpublishedAt && copy.unpublishReason === 'REJECTED')) {
      setViewState('deleted')
      return
    }

    setRecruitStatus(copy.status === 'CLOSED' ? 'CLOSED' : 'OPEN')
    setViewState('ok')

    {
      setSchool(safeJsonParse(copy.school, DEFAULT_SORT_ITEM))
      setCareer(safeJsonParse(copy.career, DEFAULT_SORT_ITEM))
      setApplytime(safeJsonParse(copy.applytime, DEFAULT_APPLYTIME))
      setApplydoc(safeJsonParse(copy.applydoc, DEFAULT_SORT_ITEM))
      setInquiry(safeJsonParse(copy.inquiry, DEFAULT_INQUIRY))

      if (config.showMinistryDetail) {
        setPart(safeJsonParse(copy.part, DEFAULT_SORT_ITEM))
        setPartDetail(safeJsonParse(copy.partDetail, DEFAULT_SORT_ITEM))
        setWorkday(safeJsonParse(copy.workday, DEFAULT_SORT_ITEM))
        setWorkTimeSunDay(safeJsonParse(copy.workTimeSunDay, DEFAULT_WORK_TIME_SUN))
        setWorkTimeWeek(safeJsonParse(copy.workTimeWeek, DEFAULT_WORK_TIME_WEEK))
        setDawnPray(safeJsonParse(copy.dawnPray, DEFAULT_SORT_ITEM))
        setPay(cleanPaySort(safeJsonParse(copy.pay, DEFAULT_PAY)))
        setInsurance(safeJsonParse(copy.insurance, DEFAULT_SORT_ITEM))
        setSeverance(safeJsonParse(copy.severance, DEFAULT_SORT_ITEM))
        setWelfare(safeJsonParse(copy.welfare, DEFAULT_SORT_ITEM))
      }

      setCustomInput(copy.customInput || '')
      setTitle(copy.title || '')
      setLink(copy.link || '')
      setSource(copy.source || '')
      // 크롤링 자동 게시 글 (server autoPublish.js — 공고 테이블 autoPublished 컬럼)
      setAutoPublished(Number(copy.autoPublished) === 1)
      setSort(copy.sort || '')
      setDate(copy.date || '')
      setLocation(copy.location || '')
      setLocationDetail(copy.locationDetail || '')
      setApplyhow(copy.applyhow || '')
      setEtcNotice(copy.etcNotice || '')
      setChurch(copy.church || '')
      setChurchLogo(copy.churchLogo || '')
      setChurchProfileId(copy.churchProfileId || null)
      setReligiousbody(copy.religiousbody || '')
      setDeadlineAt(copy.deadlineAt ? String(copy.deadlineAt).slice(0, 10) : '')
      setAddress(copy.address || '')
      setMainPastor(copy.mainPastor || '')
      setHomepage(copy.homepage || '')
      if (!useGoogleMap) {
        addressAPI(copy.address)
      }
    }
  }

  useEffect(() => {
    fetchPosts()
  }, [ID, config.apiBase])

  if (viewState === 'deleted') {
    return (
      <div className="recruit">
        <div className="inner">
          <div className="subpage__main recruit-detail-empty">
            <p>해당 채용 공고는 기업의 요청으로 삭제되었습니다.</p>
            <div className="list-btn" onClick={goToList}>
              <MdArrowBack className="list-btn__icon" />
              <span>목록</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (viewState === 'notFound') {
    return (
      <div className="recruit">
        <div className="inner">
          <div className="subpage__main recruit-detail-empty">
            <p>존재하지 않는 공고입니다.</p>
            <div className="list-btn" onClick={goToList}>
              <MdArrowBack className="list-btn__icon" />
              <span>목록</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="recruit">
      <div className="inner">
        <div className="subpage__main">
          <section className="recruit-detail-intro">
            {(sort || location) && (
              <div className="main_title_meta">
                {sort && <span className={roleMarkerClass(sort)}>{sort}</span>}
                {location && <span className="main_title_meta__text">{location} {locationDetail}</span>}
              </div>
            )}
            <div className="main_title_row">
              <h3 className="main_title mm-serif">{title}</h3>
              <div className="main_title_actions">
                <div className="list-btn" onClick={copyShareUrl}>
                  <span>{copied ? '복사됨' : '링크복사'}</span>
                </div>
                {canNativeShare && (
                  <div className="list-btn" onClick={nativeShare}>
                    <span>공유하기</span>
                  </div>
                )}
                <div className="list-btn" onClick={() => setIsShareKitOpen(true)}>
                  <span>게시판용 공유</span>
                </div>
                {canApply && (
                  <div className="list-btn list-btn--primary" onClick={handleApplyClick}>
                    <span>지원하기</span>
                  </div>
                )}
                <div className="list-btn" onClick={goToList}>
                  <MdArrowBack className="list-btn__icon" />
                  <span>목록</span>
                </div>
              </div>
            </div>
            {source !== '사역자모아' && (
              <div className="copBtn">
                <a href={link} target="_blank" className="girBtn btn">
                  <p>원본보기</p>
                  <MdArrowForwardIos />
                  <div className="recruit__religiousbody_box">
                    <img src={`${MainURL}/siteimages/schoolround/${source}.jpg`} alt="" />
                    <p className="recruit__religiousbody">{source}</p>
                  </div>
                </a>
              </div>
            )}
            <div className="post-id">
              게시글ID: {ID}
            </div>
            {aggInfo && <AggregatedLockBanner info={aggInfo} applyKind={Boolean(config.canApply)} />}
            <RecruitApplyPanel
              title={title}
              shareUrl={shareUrl}
              siteApplyBoard={Boolean(config.canApply)}
              closed={recruitStatus === 'CLOSED'}
              channel={config.canApply ? channel : null}
              inquiry={inquiry}
              originalLink={source !== '사역자모아' ? link : undefined}
              onApply={handleApplyClick}
            />
          </section>

          <section>
            <h1 className="section_title mm-serif">채용정보</h1>
            <div className="summaryBx">
              <div className="tbCol">
                <h4>사역요약</h4>
                <div className="tbList">
                  {config.showMinistryDetail && (
                    <>
                      <div className="tbLabel">구분</div>
                      <div className="tbValue">{sort}</div>
                      <div className="tbLabel">파트</div>
                      <div className="tbValue">
                        {part.length > 0 && part[0] && part[0].content && (
                          <p>
                            {sortLabelPrefix(part, part[0].sort)}
                            {part[0].content}
                          </p>
                        )}
                        {part.length > 1 && part[1] && part[1].content && (
                          <p>
                            {sortLabelPrefix(part, part[1].sort)}
                            {part[1].content}
                          </p>
                        )}
                      </div>
                      <div className="tbLabel">파트상세</div>
                      <div className="tbValue">
                        {partDetail.length > 0 && partDetail[0] && partDetail[0].content && (
                          <p>
                            {sortLabelPrefix(partDetail, partDetail[0].sort)}
                            {partDetail[0].content}
                          </p>
                        )}
                        {partDetail.length > 1 && partDetail[1] && partDetail[1].content && (
                          <p>
                            {sortLabelPrefix(partDetail, partDetail[1].sort)}
                            {partDetail[1].content}
                          </p>
                        )}
                      </div>
                      <div className="tbLabel">사례</div>
                      <div className="tbValue">
                        {pay.length > 1 ? (
                          <p>{sortLabelPrefix(pay, pay[0].sort)}{pay[0].paySort} {pay[0].selectCost}</p>
                        ) : pay[0].inputCost !== '' ? (
                          <p>{pay[0].inputCost}</p>
                        ) : (
                          <p>{pay[0].paySort} {pay[0].selectCost}</p>
                        )}
                        {pay.length > 1 && (
                          <p>{sortLabelPrefix(pay, pay[1].sort)}{pay[1].paySort} {pay[1].selectCost}</p>
                        )}
                      </div>
                    </>
                  )}
                  <div className="tbLabel">지역</div>
                  <div className="tbValue">{location} {locationDetail}</div>
                </div>
              </div>
              <div className="tbCol">
                <h4>지원자격</h4>
                <div className="tbList">
                  <div className="tbLabel">학력</div>
                  <div className="tbValue">
                    {school.length > 0 && school[0]?.content && (
                      <p>
                        {sortLabelPrefix(school, school[0].sort)}
                        {school[0].content}
                      </p>
                    )}
                    {school.length > 1 && school[1]?.content && (
                      <p>
                        {sortLabelPrefix(school, school[1].sort)}
                        {school[1].content}
                      </p>
                    )}
                  </div>
                  <div className="tbLabel">경력</div>
                  <div className="tbValue">
                    {career.length > 0 && career[0]?.content && (
                      <p>
                        {sortLabelPrefix(career, career[0].sort)}
                        {career[0].content}
                      </p>
                    )}
                    {career.length > 1 && career[1]?.content && (
                      <p>
                        {sortLabelPrefix(career, career[1].sort)}
                        {career[1].content}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {config.showMinistryDetail && (
            <section>
              <h1 className="section_title mm-serif">사역상세</h1>
              <div className="summaryBx">
                <div className="tbCol">
                  <h4>사역내용</h4>
                  <div className="tbList">
                    <div className="tbLabel">구분</div>
                    <div className="tbValue">{sort}</div>
                    <div className="tbLabel">파트</div>
                    <div className="tbValue">
                      {part.length > 0 && part[0] && part[0].content && (
                        <p>
                          {sortLabelPrefix(part, part[0].sort)}
                          {part[0].content}
                        </p>
                      )}
                      {part.length > 1 && part[1] && part[1].content && (
                        <p>
                          {sortLabelPrefix(part, part[1].sort)}
                          {part[1].content}
                        </p>
                      )}
                    </div>
                    <div className="tbLabel">파트상세</div>
                    <div className="tbValue">
                      {partDetail.length > 0 && partDetail[0] && partDetail[0].content && (
                        <p>
                          {sortLabelPrefix(partDetail, partDetail[0].sort)}
                          {partDetail[0].content}
                        </p>
                      )}
                      {partDetail.length > 1 && partDetail[1] && partDetail[1].content && (
                        <p>
                          {sortLabelPrefix(partDetail, partDetail[1].sort)}
                          {partDetail[1].content}
                        </p>
                      )}
                    </div>
                    <div className="tbLabel">사역요일</div>
                    <div className="tbValue">
                      {workday.length > 0 && workday[0] && workday[0].content && (
                        <p>
                          {sortLabelPrefix(workday, workday[0].sort)}
                          {workday[0].content}
                        </p>
                      )}
                      {workday.length > 1 && workday[1] && workday[1].content && (
                        <p>
                          {sortLabelPrefix(workday, workday[1].sort)}
                          {workday[1].content}
                        </p>
                      )}
                    </div>
                    <div className="tbLabel">사역시간(주일)</div>
                    <div className="tbValue">
                      {workTimeSunDay.length > 0 && workTimeSunDay[0] &&
                        workTimeSunDay[0].startHour && workTimeSunDay[0].startMinute &&
                        workTimeSunDay[0].endHour && workTimeSunDay[0].endMinute && (
                          <p>
                            {sortLabelPrefix(workTimeSunDay, workTimeSunDay[0].sort)}
                            {workTimeSunDay[0].startHour}:{workTimeSunDay[0].startMinute} - {workTimeSunDay[0].endHour}:{workTimeSunDay[0].endMinute}
                          </p>
                        )}
                      {workTimeSunDay.length > 1 && workTimeSunDay[1] && workTimeSunDay[1].content && (
                        <p>{sortLabelPrefix(workTimeSunDay, workTimeSunDay[1].sort)}{workTimeSunDay[1].content}</p>
                      )}
                    </div>
                    <div className="tbLabel">사역시간(평일)</div>
                    <div className="tbValue">
                      {workTimeWeek.length > 0 && workTimeWeek[0] &&
                        workTimeWeek[0].startHour && workTimeWeek[0].startMinute &&
                        workTimeWeek[0].endHour && workTimeWeek[0].endMinute && (
                          <p>
                            {sortLabelPrefix(workTimeWeek, workTimeWeek[0].sort)}
                            {workTimeWeek[0].startHour}:{workTimeWeek[0].startMinute} - {workTimeWeek[0].endHour}:{workTimeWeek[0].endMinute}
                            {workTimeWeek[0].day && ` (${workTimeWeek[0].day})`}
                          </p>
                        )}
                    </div>
                    <div className="tbLabel">새벽기도참석</div>
                    <div className="tbValue">
                      {dawnPray.length > 0 && dawnPray[0] && dawnPray[0].content && (
                        <p>
                          {sortLabelPrefix(dawnPray, dawnPray[0].sort)}
                          {dawnPray[0].content}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="tbCol">
                  <h4>사례/복지</h4>
                  <div className="tbList">
                    <div className="tbLabel">사례</div>
                    <div className="tbValue">
                      {pay.length > 1 ? (
                        <p>{sortLabelPrefix(pay, pay[0].sort)}{pay[0].paySort} {pay[0].selectCost}</p>
                      ) : pay[0].inputCost !== '' ? (
                        <p>{pay[0].inputCost}</p>
                      ) : (
                        <p>{pay[0].paySort} {pay[0].selectCost}</p>
                      )}
                      {pay.length > 1 && (
                        <p>{sortLabelPrefix(pay, pay[1].sort)}{pay[1].paySort} {pay[1].selectCost}</p>
                      )}
                    </div>
                    <div className="tbLabel">보험</div>
                    <div className="tbValue">
                      {insurance.length > 0 && insurance[0]?.content && (
                        <p>
                          {sortLabelPrefix(insurance, insurance[0].sort)}
                          {insurance[0].content}
                        </p>
                      )}
                      {insurance.length > 1 && insurance[1]?.content && (
                        <p>
                          {sortLabelPrefix(insurance, insurance[1].sort)}
                          {insurance[1].content}
                        </p>
                      )}
                    </div>
                    <div className="tbLabel">퇴직금</div>
                    <div className="tbValue">
                      {severance.length > 0 && severance[0]?.content && (
                        <p>
                          {sortLabelPrefix(severance, severance[0].sort)}
                          {severance[0].content}
                        </p>
                      )}
                      {severance.length > 1 && severance[1]?.content && (
                        <p>
                          {sortLabelPrefix(severance, severance[1].sort)}
                          {severance[1].content}
                        </p>
                      )}
                    </div>
                    <div className="tbLabel">복리후생</div>
                    <div className="tbValue">
                      {welfare.length > 0 && welfare[0]?.content && (
                        <p>
                          {sortLabelPrefix(welfare, welfare[0].sort)}
                          {welfare[0].content}
                        </p>
                      )}
                      {welfare.length > 1 && welfare[1]?.content && (
                        <p>
                          {sortLabelPrefix(welfare, welfare[1].sort)}
                          {welfare[1].content}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          <section>
            <h1 className="section_title mm-serif">지원방법</h1>
            <div className="copInfoBx">
              <div className="copInfoBx-cover">
                <div className="copInfo">
                  <div className="label">등록일</div>
                  <div className="value">
                    <p>{date}</p>
                  </div>
                  <div className="label">기간</div>
                  <div className="value">
                    {applytime.startDay !== '' && applytime.endDay !== '' ? (
                      <p>{applytime.startDay} ~ {applytime.endDay} ({applytime.daySort})</p>
                    ) : (
                      <p>{applytime.daySort}</p>
                    )}
                  </div>
                  <div className="label">서류</div>
                  <div className="value">
                    {applydoc.length > 0 && applydoc[0]?.content && (
                      <p>
                        {sortLabelPrefix(applydoc, applydoc[0].sort)}
                        {applydoc[0].content}
                      </p>
                    )}
                    {applydoc.length > 1 && applydoc[1]?.content && (
                      <p>
                        {sortLabelPrefix(applydoc, applydoc[1].sort)}
                        {applydoc[1].content}
                      </p>
                    )}
                  </div>
                  <div className="label">지원방법</div>
                  <div className="value">{canApply ? applyhowWithSite(applyhow) : applyhow}</div>
                  <div className="label">담당자</div>
                  <div className="value">
                    <p>이름 : {inquiry.inquiryName}</p>
                    <p>
                      이메일 : {inquiry.email && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(inquiry.email).trim())
                        ? <a href={`mailto:${String(inquiry.email).trim()}?subject=${encodeURIComponent(`[지원] ${title}`)}`}>{inquiry.email}</a>
                        : inquiry.email}
                    </p>
                    <p>전화번호 : {shownPhone}</p>
                  </div>
                  <div className="label">기타사항</div>
                  <div className="value value--pre-line">{etcNotice}</div>
                </div>
              </div>
            </div>
          </section>

          <section id="tab03" className="recruit-detail-content">
            <h1 className="section_title mm-serif">상세내용</h1>
            <div className="copInfoBx">
              <div
                className="custom-html-content"
                dangerouslySetInnerHTML={{ __html: shownCustomInput }}
              />
              {autoPublished && /^https?:\/\//i.test(link) && (
                <div className="recruit-original-link">
                  <a href={link} target="_blank" rel="noopener noreferrer" className="recruit-original-link__btn">
                    원문보기
                    <MdArrowForwardIos />
                  </a>
                </div>
              )}
            </div>
          </section>

          <section id="tab03">
            <h1 className="section_title mm-serif">교회 정보</h1>
            <div className="copInfoBx">
              <div className="header">
                <h4 className="church">{church}</h4>
                {churchProfileId && (
                  <Link to={`/church/${churchProfileId}`} className="church-profile-link">
                    교회 정보 · 청빙 이력 보기 ›
                  </Link>
                )}
              </div>
              <div className="copInfoBx-cover">
                <div className="copLogo">
                  {!shownLogo ? (
                    <div className="emptyLogo">
                      <p>사역자모아</p>
                    </div>
                  ) : (
                    <img
                      className="churchLogo"
                      src={`${MainURL}/${config.churchLogoImagePath}/${shownLogo}`}
                      alt="로고"
                      onError={() => { setChurchLogo('') }}
                    />
                  )}
                </div>
                <div className="copInfo">
                  <div className="label">교단</div>
                  <div className="value">
                    <div className="recruit__religiousbody_box">
                      <img src={`${MainURL}/${config.religiousbodyDetailImagePath}/${religiousbody}.jpg`} alt="" />
                      <p className="recruit__religiousbody">{religiousbody}</p>
                    </div>
                  </div>
                  <div className="label">지역</div>
                  <div className="value">{location} {locationDetail}</div>
                  <div className="label">주소</div>
                  <div className="value">{address}</div>
                  <div className="label">담임목사</div>
                  <div className="value">{mainPastor}</div>
                  <div className="label">홈페이지</div>
                  {homepage && homepage !== '' && (
                    <div className="value value--inline">
                      <p>{homepage}</p>
                      <a
                        href={homepage.includes('http') ? homepage : `http://${homepage}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="homepage-link"
                      >
                        <p className="homepage-link__text">바로가기</p>
                        <MdArrowForwardIos className="homepage-link__icon" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {aggInfo && <AggregatedSourceNotice info={aggInfo} />}

          <div className="maparea">
            {useGoogleMap ? (
              <OverseasGoogleMap address={address} />
            ) : mapLoaded ? (
              <div id="map" className="map-canvas" ref={mapElement} />
            ) : (
              <div className="map-fallback">
                주소가 정확하지 않아 지도를 표시할 수 없습니다.
              </div>
            )}
          </div>

          <div className="bottom-list-btn-wrap">
            <div className="bottom-list-btn-inner">
              <div className="list-btn list-btn--bottom" onClick={goToList}>
                <MdArrowBack className="list-btn__icon" />
                <span>목록</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {canApply && (
        <RecruitApplyModal
          isOpen={isApplyModalOpen}
          onClose={() => setIsApplyModalOpen(false)}
          recruitId={ID || ''}
          recruitType={applyType}
          userAccount={userData.userAccount}
          isLogin={Boolean(isLogin && userData.userAccount)}
          church={church}
          channel={channel}
        />
      )}

      <RecruitShareKit
        isOpen={isShareKitOpen}
        onClose={() => setIsShareKitOpen(false)}
        data={shareKitData}
      />
    </div>
  )
}
